import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { useBrand } from '../brand/BrandProvider';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { workspaceKey } from '../lib/workspaceQueryKey';
import type { AnnouncementsResponse } from '../api/types';
import { countUnreadAnnouncements, sortAnnouncements, visibleAnnouncements } from './unread';

export const announcementsQueryKey = (workspaceId: string | null) => workspaceKey('announcements', workspaceId);

/** Five minutes, like `useFeedbackUnread` (docs/42 §5): this mounts from `Layout`, so without a
 *  stale window every window focus would refetch. */
const ANNOUNCEMENTS_STALE_TIME = 5 * 60_000;

/**
 * The active workspace's announcements. Gated on a resolved workspace for the reason
 * `useFeedbackQuery` gives: `Layout` mounts it, including on `/select-workspace` with none.
 * Keyed by workspace because appcore's answer differs by workspace (family or firm subset).
 */
export function useAnnouncementsQuery() {
  const token = useAccessToken();
  const { activeWorkspaceId } = useWorkspace();
  return useQuery({
    queryKey: announcementsQueryKey(activeWorkspaceId),
    queryFn: () => createApiClient(token!).get<AnnouncementsResponse>('/announcements'),
    enabled: !!token && !!activeWorkspaceId,
    staleTime: ANNOUNCEMENTS_STALE_TIME,
  });
}

/**
 * The nav badge's count, from the same cache entry the page reads. `0` until the brand has
 * settled: `hideOnBrandedHosts` cannot be applied before then, so a count taken earlier could
 * include an item the page will then hide, a badge nobody can clear.
 */
export function useAnnouncementsUnread(): { count: number } {
  const { data } = useAnnouncementsQuery();
  const { profile } = useWorkspace();
  const { isWhiteLabel, settled } = useBrand();
  const seenAt = profile?.preferences?.announcementsSeenAt;
  const count = useMemo(() => {
    if (!data || !settled) return 0;
    return countUnreadAnnouncements(visibleAnnouncements(data.items, isWhiteLabel), seenAt, Date.now());
  }, [data, settled, isWhiteLabel, seenAt]);
  return { count };
}

/** What the page renders: visible items, newest first, once the brand has settled. */
export function useVisibleAnnouncements() {
  const q = useAnnouncementsQuery();
  const { isWhiteLabel, settled } = useBrand();
  const items = useMemo(
    () => (q.data && settled ? sortAnnouncements(visibleAnnouncements(q.data.items, isWhiteLabel)) : []),
    [q.data, settled, isWhiteLabel],
  );
  return { query: q, items, viewerAudience: q.data?.viewerAudience ?? 'families', settled };
}
